// Search landing pages: one address and one body of text per search that families type into Google
// ("Vanniyar matrimony Puducherry", "wedding photographers in Cuddalore", "திருமண பொருத்தம்" ...).
// Each page is shown by pages/SeoLanding/SeoLanding.jsx, gets its own title / description / canonical from components/Seo/RouteSeo.jsx,
// and is listed in server/utils/sitemap.js (keep the three lists of addresses in step when adding a page).
// Only say things that are true of the platform: verified profiles, privacy first, free registration, horoscope matching, family-managed profiles,
// wedding service listings with booking requests. No member counts or "#1" claims.

export const MATRIMONY_POINTS = [
    ['Verified profiles', 'Our team reviews profiles, and members can add Aadhaar verification, so you meet genuine people.'],
    ['Privacy first', 'Phone numbers and documents stay protected. You decide who can contact you.'],
    ['Horoscope matching', 'Check Thirumana Porutham, star and rasi compatibility before you take a match forward.'],
    ['Made for families', 'Parents and relatives can create and manage a profile for a son, daughter, brother or sister.'],
];

export const MATRIMONY_STEPS = [
    'Create a free profile with your details, photos and partner preferences.',
    'Browse verified profiles and send an interest to the ones you like.',
    'Compare horoscopes, then talk with the family when both sides are interested.',
];

export const WEDDING_POINTS = [
    ['Compare in one place', 'See photos, price range, location and reviews of each vendor side by side.'],
    ['Check dates', 'Pick your wedding date and see which vendors are free.'],
    ['Send a booking request', 'Ask the vendor directly from the website. Packages and themes are shown on the vendor page.'],
    ['Local vendors', 'Vendors list their own city and district, so you find people who work near your venue.'],
];

export const WEDDING_STEPS = [
    'Open the wedding services page and choose the category you need.',
    'Filter by state, district and budget, and open the vendors you like.',
    'Check the date, read the packages and send a booking request.',
];

const matrimony = ({ slug, community, place, religion, caste, subCaste, state, district, about, tips, faqs }) => ({
    path: `/matrimony/${slug}`,
    group: 'Matrimony',
    name: `${community} Matrimony${place ? ` in ${place}` : ''}`,
    metaTitle: `${community} Matrimony${place ? ` in ${place}` : ''} | Verified Brides & Grooms`,
    description: `Find verified ${community} brides and grooms${place ? ` in ${place}` : ''}. Free registration, horoscope matching and privacy-first contact on Gettimelam Matrimony.`,
    h1: `${community} Matrimony${place ? ` in ${place}` : ''}`,
    lead: about,
    points: MATRIMONY_POINTS,
    sectionTitle: `How to find a ${community} match`,
    steps: MATRIMONY_STEPS,
    tips,
    faqs,
    browse: `/browse?${new URLSearchParams({ religion, ...(caste ? { caste } : {}), ...(subCaste ? { subCaste } : {}), ...(state ? { state } : {}), ...(district ? { district } : {}) }).toString()}`,
    browseLabel: `Browse ${community} profiles`,
});

const wedding = ({ slug, service, place, about, tips, faqs }) => ({
    path: `/wedding-services/${slug}`,
    group: 'Wedding services',
    name: `${service} in ${place}`,
    metaTitle: `${service} in ${place} | Compare & Book`,
    description: `Compare ${service.toLowerCase()} in ${place}: see photos, price range and available dates, then send a booking request on Gettimelam Matrimony.`,
    h1: `${service} in ${place}`,
    lead: about,
    points: WEDDING_POINTS,
    sectionTitle: `How to book ${service.toLowerCase()} in ${place}`,
    steps: WEDDING_STEPS,
    tips,
    faqs,
    browse: '/services',
    browseLabel: 'See wedding services',
});

export const SEO_PAGES = [
    matrimony({
        slug: 'vanniyar-puducherry', community: 'Vanniyar', place: 'Puducherry', religion: 'Hindu', caste: 'Vanniyar', state: 'Puducherry',
        about: [
            'Looking for a Vanniyar bride or groom in Puducherry? Gettimelam Matrimony is a Tamil matrimony platform based in Puducherry, built for families who want a match from their own community and a process they can trust.',
            'You can search Vanniyar profiles from Puducherry, Cuddalore, Villupuram and the rest of Tamil Nadu. Every profile shows education, work, family details and star and rasi, so you can shortlist quickly.',
        ],
        tips: ['Add a clear photo and your star and rasi: profiles with these details get more interest.', 'Write your partner preference honestly, such as age, education and place, so the matches you see are useful.', 'Keep your family details up to date. Parents can manage the profile for you.'],
        faqs: [
            ['Can I search only Vanniyar profiles in Puducherry?', 'Yes. Use the button on this page. It opens the profiles list already filtered to the Vanniyar community and Puducherry. You can change the filters at any time.'],
            ['Is registration free?', 'Yes, creating a profile and browsing are free. A Premium membership adds extras such as unlimited contact views, interests and messages.'],
            ['Can my parents create the profile for me?', 'Yes. A parent, brother or sister can create and manage a profile on your behalf.'],
            ['Can I check horoscope match before talking to a family?', 'Yes. Our horoscope matching page shows the 10 poruthams for any two stars, free of cost.'],
        ],
    }),
    matrimony({
        slug: 'kongu-vellalar-coimbatore', community: 'Kongu Vellalar', place: 'Coimbatore', religion: 'Hindu', caste: 'Vellalar', subCaste: 'Kongu Vellala Gounder', state: 'Tamil Nadu', district: 'Coimbatore',
        about: [
            'Gettimelam Matrimony helps Kongu Vellalar families in Coimbatore, Erode, Tiruppur, Salem and nearby towns find a bride or groom they can trust. Profiles list education, profession, family background, star and rasi.',
            'Start with the Coimbatore filter below, then widen to other Kongu districts if you want more choice.',
        ],
        tips: ['Choose your sub-community when you register if you want matches within it.', 'Add your work city. Many families in the Kongu region also look at Chennai, Bengaluru and abroad.', 'Upload your horoscope so families can check porutham quickly.'],
        faqs: [
            ['Does the site list Kongu Vellala Gounder profiles?', 'Members can choose Vellalar and the Kongu Vellala Gounder sub-community when they register, and you can search by it.'],
            ['How do I know a profile is genuine?', 'Profiles are reviewed by our team, and members can add Aadhaar verification. Verified profiles are marked.'],
            ['Is my phone number shown to everyone?', 'No. Contact details are protected, and they are shown only under the rules of the member\'s plan.'],
            ['Can I search by city, such as Coimbatore?', 'Yes. This page opens the list already filtered to Coimbatore. You can change the district or state.'],
        ],
    }),
    matrimony({
        slug: 'christian-tamil-nadu', community: 'Christian', place: 'Tamil Nadu', religion: 'Christian', state: 'Tamil Nadu',
        about: [
            'Gettimelam Matrimony brings Tamil Christian families together in one place. Search brides and grooms from Chennai, Coimbatore, Madurai, Tirunelveli, Kanyakumari, Puducherry and other districts.',
            'Profiles show education, profession, family details and star and rasi. In the "about me" section families can write what matters to them, such as faith and church life.',
        ],
        tips: ['Write a short, honest "about me": faith, family and what you look for.', 'Add a recent photo and your preferred age range.', 'Keep your family details up to date. Parents can manage the profile for you.'],
        faqs: [
            ['Can I find Christian brides and grooms across Tamil Nadu?', 'Yes. This page opens Christian profiles in Tamil Nadu, and you can narrow by district, age and education.'],
            ['Is there a separate site for Christian members?', 'No. It is one site. The images and layout change to match your faith when you sign in.'],
            ['Is registration free?', 'Yes. Creating a profile and browsing are free.'],
            ['Can I describe my faith and family in my profile?', 'Yes. Use the "about me" section to tell families about your faith, family and what you look for.'],
        ],
    }),
    matrimony({
        slug: 'muslim-tamil-nadu', community: 'Muslim', place: 'Tamil Nadu', religion: 'Muslim', state: 'Tamil Nadu',
        about: [
            'Gettimelam Matrimony helps Tamil Muslim families find a suitable match with respect for faith, family and privacy. Browse brides and grooms from Chennai, Coimbatore, Trichy, Madurai, Kayalpatnam, Puducherry and more.',
            'Profiles show education, work, family details and community information. Parents and guardians can create and manage the profile for a son or daughter.',
        ],
        tips: ['Add your family details and what you look for in a partner.', 'Use a clear photo that you are comfortable sharing. You decide who can see more.', 'Keep your contact details protected and share them only when a family is serious.'],
        faqs: [
            ['Can I find Muslim brides and grooms in Tamil Nadu?', 'Yes. This page opens Muslim profiles in Tamil Nadu, and you can narrow the list by district, age and education.'],
            ['Is my privacy protected?', 'Yes. Phone numbers and documents are protected, and you control who can contact you.'],
            ['Can parents manage a profile?', 'Yes. A parent or guardian can create and manage the profile.'],
            ['Is registration free?', 'Yes. Creating a profile and browsing are free.'],
        ],
    }),
    matrimony({
        slug: 'iyer-chennai', community: 'Iyer', place: 'Chennai', religion: 'Hindu', caste: 'Tamil Brahmin (Iyer)', state: 'Tamil Nadu', district: 'Chennai',
        about: [
            'Gettimelam Matrimony lists Tamil Brahmin (Iyer) brides and grooms from Chennai and across Tamil Nadu. Profiles include star, rasi, education and work, so families can look at porutham early.',
            'Use the Chennai filter below to see profiles near you, or widen the search to other cities.',
        ],
        tips: ['Add your star and rasi. Many families check these first.', 'Upload your horoscope so a quick porutham check is possible.', 'Mention your work city and whether you are open to relocating.'],
        faqs: [
            ['Does Gettimelam list Iyer profiles?', 'Yes. Members choose Tamil Brahmin (Iyer) as their community when they register, and you can search by it.'],
            ['Can I check horoscope match?', 'Yes. Our horoscope matching page shows the 10 poruthams for any two stars, free of cost.'],
            ['Can I search only Chennai profiles?', 'Yes. This page opens the list already filtered to Chennai. You can change the district, state or city in the filters.'],
            ['Is registration free?', 'Yes. Creating a profile and browsing are free.'],
        ],
    }),

    wedding({
        slug: 'photographers-cuddalore', service: 'Wedding Photographers', place: 'Cuddalore',
        about: [
            'Planning a wedding in Cuddalore? Compare wedding photographers and videographers who work in Cuddalore and nearby areas such as Chidambaram, Neyveli and Puducherry, all in one place.',
            'Look at each photographer\'s photos and price range, check whether your date is free, and send a booking request from the website.',
        ],
        tips: ['Ask what is included: number of photographers, hours, edited photos, album and video.', 'Book early for muhurtham dates, because good photographers fill up fast.', 'Ask to see a full wedding they have shot, not only highlights.'],
        faqs: [
            ['How do I book a photographer in Cuddalore?', 'Open the vendor, check the date and send a booking request. The vendor replies with details.'],
            ['Can I see prices?', 'Each vendor shows a price range. Final prices depend on the package and your requirements.'],
            ['Can I compare more than one photographer?', 'Yes. Open the vendors you like and compare their photos, packages and price ranges.'],
        ],
    }),
    wedding({
        slug: 'mandap-puducherry', service: 'Wedding Mandap and Decoration', place: 'Puducherry',
        about: [
            'Find wedding mandap, stage and flower decoration vendors in Puducherry. Compare designs and price ranges, and ask for packages for your muhurtham.',
            'Many vendors also offer lighting, entrance arches and reception stage decoration. You can check which of them are free on your wedding date.',
        ],
        tips: ['Share the hall size and the number of guests when you ask for a quote.', 'Ask whether flowers are fresh or artificial and what the setup and removal time is.', 'Check that the vendor has worked in your hall before.'],
        faqs: [
            ['Can I find mandap decorators in Puducherry?', 'Yes. Open the wedding services page, choose the decoration category and filter by Puducherry.'],
            ['Can I book for a specific date?', 'Yes. Enter the date and the page shows which vendors are available.'],
            ['Do vendors give package options?', 'Many vendors list packages and themes on their page. You can also ask the vendor for a custom quote.'],
        ],
    }),
    wedding({
        slug: 'catering-salem', service: 'Wedding Catering', place: 'Salem',
        about: [
            'Compare wedding catering services in Salem for muhurtham breakfast, lunch and reception dinner. See each caterer\'s menu themes and price range, and send a request for your date.',
            'You can look for traditional Tamil vegetarian meals as well as other menus, depending on what each caterer offers.',
        ],
        tips: ['Give the guest count and the menu you want. The quote depends on both.', 'Ask for a tasting if the caterer offers one.', 'Check how many servers are included and when the food will be ready.'],
        faqs: [
            ['Can I find caterers in Salem?', 'Yes. Open the wedding services page, choose catering and filter by Salem.'],
            ['Do caterers show menus?', 'Many show their menus and themes on their vendor page. You can also ask them for a custom menu.'],
            ['How do I send a request?', 'Open the caterer, check your date and send a booking request from the page.'],
        ],
    }),

    {
        path: '/ta/thirumana-porutham',
        group: 'தமிழ்',
        lang: 'ta',
        name: 'திருமண பொருத்தம்',
        metaTitle: 'திருமண பொருத்தம் பார்ப்பது எப்படி? | இலவச ஜாதகப் பொருத்தம்',
        description: '10 பொருத்தங்கள் என்ன, நட்சத்திரப் பொருத்தம், தோஷம் பற்றி எளிய தமிழில் அறிந்து, Gettimelam Matrimony-இல் இலவசமாக ஜாதகப் பொருத்தம் பாருங்கள்.',
        h1: 'திருமண பொருத்தம் – இலவச ஜாதகப் பொருத்தம்',
        lead: [
            'திருமணத்திற்கு முன் மணமகன் மற்றும் மணமகளின் ஜாதகங்களை ஒப்பிட்டுப் பார்ப்பதே திருமண பொருத்தம் ஆகும். தமிழ் மரபில் நட்சத்திரம் மற்றும் ராசியை அடிப்படையாகக் கொண்டு பத்து பொருத்தங்கள் பார்க்கப்படுகின்றன.',
            'Gettimelam Matrimony-இல் இரு நட்சத்திரங்களைத் தேர்வு செய்து உடனே பொருத்த முடிவைப் பார்க்கலாம். இறுதி முடிவுக்கு உங்கள் குடும்ப ஜோதிடரையும் கலந்தாலோசிப்பது நல்லது.',
        ],
        points: [
            ['தினப் பொருத்தம்', 'உடல் நலம் மற்றும் ஆயுள் நலன்.'],
            ['கணப் பொருத்தம்', 'குணம் மற்றும் மனப்பாங்கு ஒத்துப்போகுமா என்பது.'],
            ['மகேந்திரப் பொருத்தம்', 'குழந்தைப் பேறு மற்றும் குடும்ப வளர்ச்சி.'],
            ['ஸ்திரீ தீர்க்கப் பொருத்தம்', 'மணமகளின் நலன் மற்றும் செழிப்பு.'],
            ['யோனிப் பொருத்தம்', 'தாம்பத்திய இணக்கம்.'],
            ['ராசிப் பொருத்தம்', 'குடும்ப வம்ச வளர்ச்சி.'],
            ['ராசி அதிபதிப் பொருத்தம்', 'இருவரின் ராசி அதிபதிகளின் நட்பு.'],
            ['வசியப் பொருத்தம்', 'இருவருக்கும் இடையிலான பரஸ்பர ஈர்ப்பு.'],
            ['ரஜ்ஜுப் பொருத்தம்', 'திருமண வாழ்வின் நீடித்த தன்மை. இது மிக முக்கியமான பொருத்தமாகக் கருதப்படுகிறது.'],
            ['வேதைப் பொருத்தம்', 'துன்பம் அல்லது தடைகள் இல்லாமை.'],
        ],
        sectionTitle: 'இந்த 10 பொருத்தங்கள்',
        steps: [
            'ஜாதகப் பொருத்தம் பக்கத்தைத் திறக்கவும்.',
            'மணமகன் மற்றும் மணமகளின் நட்சத்திரம் மற்றும் ராசியைத் தேர்வு செய்யவும்.',
            'பத்து பொருத்தங்களின் முடிவையும் செவ்வாய் தோஷ நிலையையும் பார்க்கவும்.',
        ],
        tips: ['பொதுவாக பத்தில் குறைந்தது ஆறு பொருத்தங்கள் இருந்தால் நல்லது என்று கருதப்படுகிறது.', 'ரஜ்ஜு மற்றும் வேதை பொருத்தங்களை ஜோதிடர்கள் குறிப்பாகக் கவனிப்பார்கள்.', 'இந்த முடிவு ஒரு வழிகாட்டி மட்டுமே. இறுதி முடிவை உங்கள் குடும்ப ஜோதிடரிடம் உறுதி செய்யுங்கள்.'],
        faqs: [
            ['எத்தனை பொருத்தங்கள் இருந்தால் திருமணம் செய்யலாம்?', 'பொதுவாக பத்தில் குறைந்தது ஆறு பொருத்தங்கள் இருந்தால் நல்லது என்று கருதப்படுகிறது. ரஜ்ஜு, வேதை போன்றவை முக்கியம் என்பதால் ஜோதிடரிடம் உறுதி செய்வது நல்லது.'],
            ['செவ்வாய் தோஷம் என்றால் என்ன?', 'ஜாதகத்தில் செவ்வாய் கிரகம் குறிப்பிட்ட இடங்களில் இருப்பதை செவ்வாய் தோஷம் என்று கூறுவர். இதற்கான பரிகாரம் குறித்து ஜோதிடரிடம் கேட்கவும்.'],
            ['Gettimelam-இல் பொருத்தம் பார்ப்பது இலவசமா?', 'ஆம். ஜாதகப் பொருத்தம் பக்கத்தில் இரு நட்சத்திரங்களைத் தேர்வு செய்து இலவசமாகப் பொருத்தம் பார்க்கலாம்.'],
            ['பொருத்தம் பார்க்க என்ன தேவை?', 'மணமகன் மற்றும் மணமகளின் நட்சத்திரம் மற்றும் ராசி தெரிந்தால் போதும். பிறந்த விவரங்கள் இருந்தால் அவற்றிலிருந்தும் கணிக்கலாம்.'],
        ],
        browse: '/horoscope',
        browseLabel: 'ஜாதகப் பொருத்தம் பார்க்க',
    },
    {
        path: '/ta/tamil-thirumana-thagaval-maiyam',
        group: 'தமிழ்',
        lang: 'ta',
        name: 'தமிழ் திருமண தகவல் மையம்',
        metaTitle: 'தமிழ் திருமண தகவல் மையம் | மணமகன் மணமகள் வரன் தேடல்',
        description: 'தமிழ் மணமகன் மற்றும் மணமகள் வரன்களை இலவசமாகப் பதிவு செய்து தேடுங்கள். சரிபார்க்கப்பட்ட விவரங்கள், ஜாதகப் பொருத்தம், திருமணச் சேவைகள் Gettimelam Matrimony-இல்.',
        h1: 'தமிழ் திருமண தகவல் மையம்',
        lead: [
            'Gettimelam Matrimony என்பது தமிழ்க் குடும்பங்களுக்கான திருமண தகவல் மையம். புதுச்சேரியை மையமாகக் கொண்டு செயல்படும் இந்தத் தளத்தில் மணமகன் மற்றும் மணமகள் வரன்களை இலவசமாகப் பதிவு செய்து தேடலாம்.',
            'ஒவ்வொரு விவரப் பக்கத்திலும் கல்வி, தொழில், குடும்ப விவரம், நட்சத்திரம், ராசி ஆகியவை இருக்கும். இந்து, கிறிஸ்தவ, முஸ்லிம் குடும்பங்கள் அனைவரும் இங்கே வரன் தேடலாம்.',
        ],
        points: [
            ['சரிபார்க்கப்பட்ட விவரங்கள்', 'எங்கள் குழு விவரங்களைப் பரிசீலிக்கிறது. ஆதார் சரிபார்ப்பும் செய்யலாம்.'],
            ['தனிநபர் தகவல் பாதுகாப்பு', 'தொலைபேசி எண் மற்றும் ஆவணங்கள் பாதுகாக்கப்படுகின்றன.'],
            ['ஜாதகப் பொருத்தம்', 'பத்து பொருத்தங்களை இலவசமாகப் பார்க்கலாம்.'],
            ['குடும்பத்தினர் பதிவு செய்யலாம்', 'பெற்றோர் அல்லது உறவினர் மகன், மகள், சகோதர சகோதரிக்காகப் பதிவு செய்யலாம்.'],
        ],
        sectionTitle: 'வரன் தேடுவது எப்படி?',
        steps: [
            'இலவசமாகப் பதிவு செய்து உங்கள் விவரங்கள் மற்றும் புகைப்படத்தைச் சேர்க்கவும்.',
            'மதம், சமூகம், ஊர் ஆகியவற்றின் அடிப்படையில் வரன்களைத் தேடவும்.',
            'பிடித்த வரனுக்கு விருப்பம் அனுப்பி, ஜாதகம் பார்த்து குடும்பத்தினருடன் பேசுங்கள்.',
        ],
        tips: ['தெளிவான புகைப்படம் சேர்க்கவும்.', 'நட்சத்திரம், ராசி மற்றும் ஜாதகத்தைப் பதிவேற்றவும்.', 'எதிர்பார்ப்புகளை நேர்மையாக எழுதவும்.'],
        faqs: [
            ['பதிவு செய்வது இலவசமா?', 'ஆம். பதிவு செய்வதும் வரன்களைப் பார்ப்பதும் இலவசம். கூடுதல் வசதிகளுக்கு பிரீமியம் உறுப்பினர் திட்டம் உள்ளது.'],
            ['எந்த ஊர்களிலிருந்து வரன்கள் உள்ளன?', 'தமிழ்நாடு மற்றும் புதுச்சேரியில் உள்ள தமிழ்க் குடும்பங்களின் வரன்களை மாநிலம், மாவட்டம், ஊர் வாரியாகத் தேடலாம்.'],
            ['தமிழில் பயன்படுத்தலாமா?', 'ஆம். தளத்தின் மொழியைத் தமிழுக்கு மாற்றிக் கொள்ளலாம்.'],
            ['திருமணச் சேவைகளும் உள்ளதா?', 'ஆம். மண்டபம், சமையல், புகைப்படம், அலங்காரம் போன்ற திருமணச் சேவைகளையும் ஒப்பிட்டுப் பதிவு செய்யலாம்.'],
        ],
        browse: '/register',
        browseLabel: 'இலவசமாகப் பதிவு செய்ய',
    },
];

export const seoPageFor = path => {
    const clean = path.length > 1 ? path.replace(/\/+$/, '') : path;
    return SEO_PAGES.find(page => page.path === clean) || null;
};
